import {
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ClientInfoService } from './client-info.service';

/**
 * HTTP Interceptor der Client-Informationen als Headers zu jedem API-Request hinzufügt
 *
 * Hinzugefügte Headers:
 * - X-Client-Platform: 'web' | 'android' | 'ios'
 * - X-Client-Version: App-Version (z.B. "1.0.36")
 * - X-Client-Build: Build-Nummer (nur native)
 * - X-Client-Os: Betriebssystem (z.B. "Android", "iOS", "Windows")
 * - X-Client-Os-Version: OS-Version (z.B. "14" für Android 14)
 * - X-Client-Device: Gerätemodell (z.B. "SM-S918B")
 * - X-Client-Manufacturer: Hersteller (z.B. "Samsung")
 * - X-Client-Browser: Browser-Name (nur web)
 * - X-Client-Browser-Version: Browser-Version (nur web)
 * - X-Client-Device-Type: Gerätetyp (z.B. "Mobile", "Desktop", "Tablet")
 */
export function clientInfoInterceptor(
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const clientInfoService = inject(ClientInfoService);

  // Skip für statische Assets (Übersetzungen, Fonts, Bilder)
  if (isStaticAssetRequest(req)) {
    return next(req);
  }

  // Nur für API-Requests (nicht für externe URLs)
  if (!isApiRequest(req)) {
    return next(req);
  }

  // Client-Info synchron abrufen (sollte bereits initialisiert sein)
  const clientInfo = clientInfoService.getClientInfoSync();

  if (!clientInfo) {
    // Falls noch nicht initialisiert, Request ohne Headers durchlassen
    // (passiert nur bei den allerersten Requests beim App-Start)
    return next(req);
  }

  // Headers hinzufügen
  const headers = clientInfoService.formatAsHeaders(clientInfo);
  let modifiedRequest = req;

  for (const [key, value] of Object.entries(headers)) {
    if (value) {
      modifiedRequest = modifiedRequest.clone({
        headers: modifiedRequest.headers.set(key, value),
      });
    }
  }

  return next(modifiedRequest);
}

/**
 * Prüft ob es sich um einen API-Request handelt
 */
function isApiRequest(request: HttpRequest<any>): boolean {
  const url = request.url.toLowerCase();

  // Nur Requests an unsere API
  return url.includes('/api/') || url.includes('api.');
}

/**
 * Prüft ob es sich um einen statischen Asset-Request handelt
 */
function isStaticAssetRequest(request: HttpRequest<any>): boolean {
  const url = request.url.toLowerCase();

  return (
    url.includes('/assets/i18n/') ||
    (url.endsWith('.json') && (url.includes('/i18n/') || url.includes('/assets/'))) ||
    url.endsWith('.woff') ||
    url.endsWith('.woff2') ||
    url.endsWith('.ttf') ||
    url.endsWith('.eot') ||
    url.endsWith('.svg') ||
    url.endsWith('.png') ||
    url.endsWith('.jpg') ||
    url.endsWith('.jpeg') ||
    url.endsWith('.gif') ||
    url.endsWith('.webp') ||
    url.endsWith('.css') ||
    (url.endsWith('.js') && !url.includes('/api/'))
  );
}
